import React from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { Controller } from "react-hook-form";

// Base form field wrapper with consistent styling
export const FormField = ({
  children,
  label,
  name,
  control,
  rules,
  error,
  helperText,
  required = false,
  fullWidth = true,
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onChange, value, ref },
        fieldState: { error: fieldError },
      }) => (
        <Box
          sx={{
            width: fullWidth ? "100%" : "auto",
            mb: 2,
            ...sx,
          }}
        >
          {React.cloneElement(children, {
            label,
            value: value || "",
            onChange,
            inputRef: ref,
            error: !!fieldError || !!error,
            helperText: fieldError?.message || helperText,
            required,
            fullWidth,
            size: isMobile ? "medium" : "small",
          })}
        </Box>
      )}
    />
  );
};

// Enhanced text field with consistent styling
export const TextFieldEnhanced = ({
  label,
  placeholder,
  name,
  control,
  type = "text",
  multiline = false,
  rows = 4,
  required = false,
  rules = {},
  fullWidth = true,
  sx = {},
}) => {
  const theme = useTheme();

  return (
    <FormField
      name={name}
      control={control}
      rules={rules}
      required={required}
      fullWidth={fullWidth}
      sx={sx}
    >
      <Box
        component="input"
        sx={{
          width: "100%",
          padding: theme.spacing(1.5, 2),
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.borderRadius,
          fontSize: "1rem",
          fontFamily: theme.typography.fontFamily,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          transition: "all 0.2s ease",
          "&:focus": {
            outline: "none",
            borderColor: theme.palette.primary.dark,
            boxShadow: `0 0 0 3px ${theme.palette.primary.dark}20`,
          },
          "&:hover": {
            borderColor: theme.palette.grey[400],
          },
          "&::placeholder": {
            color: theme.palette.text.disabled,
          },
          ...(multiline && {
            resize: "vertical",
            minHeight: `${rows * 1.5}rem`,
          }),
        }}
        placeholder={placeholder}
        type={type}
      />
    </FormField>
  );
};

// Enhanced select field
export const SelectFieldEnhanced = ({
  label,
  name,
  control,
  options = [],
  required = false,
  rules = {},
  fullWidth = true,
  sx = {},
}) => {
  const theme = useTheme();

  return (
    <FormField
      name={name}
      control={control}
      rules={rules}
      required={required}
      fullWidth={fullWidth}
      sx={sx}
    >
      <Box
        component="select"
        sx={{
          width: "100%",
          padding: theme.spacing(1.5, 2),
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.borderRadius,
          fontSize: "1rem",
          fontFamily: theme.typography.fontFamily,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          transition: "all 0.2s ease",
          cursor: "pointer",
          "&:focus": {
            outline: "none",
            borderColor: theme.palette.primary.dark,
            boxShadow: `0 0 0 3px ${theme.palette.primary.dark}20`,
          },
          "&:hover": {
            borderColor: theme.palette.grey[400],
          },
        }}
      >
        <option value="">{label}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Box>
    </FormField>
  );
};

// Form section wrapper
export const FormSection = ({ children, title, subtitle, sx = {} }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        mb: 4,
        p: 3,
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.grey[200]}`,
        ...sx,
      }}
    >
      {(title || subtitle) && (
        <Box sx={{ mb: 3 }}>
          {title && (
            <Box
              component="h3"
              sx={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: subtitle ? 0.5 : 0,
              }}
            >
              {title}
            </Box>
          )}
          {subtitle && (
            <Box
              component="p"
              sx={{
                fontSize: "0.875rem",
                color: theme.palette.text.secondary,
                m: 0,
              }}
            >
              {subtitle}
            </Box>
          )}
        </Box>
      )}
      {children}
    </Box>
  );
};

// Form actions wrapper
export const FormActions = ({ children, sx = {} }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 2,
        justifyContent: "flex-end",
        alignItems: { xs: "stretch", sm: "center" },
        mt: 4,
        pt: 3,
        borderTop: `1px solid ${theme.palette.grey[200]}`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default {
  FormField,
  TextFieldEnhanced,
  SelectFieldEnhanced,
  FormSection,
  FormActions,
};
